import React, { useEffect, useRef, useState } from 'react';
import { elements, overlays } from '@ecosystem/newron-design-system';
import { ContextManager } from './manager';
import { Context, Entity, SettingDefinition, SettingsValues } from '../types';
import { useOnOutsideClick } from '../tools/outsideclick.hook';
import { ContentProviderRegister } from '../newron-core';
import { EditProvider } from './edit.provider';
import { useContextTracking } from './contextTracking.hook';
import { useMobile } from './mobile/mobile.provider';
import classNames from 'classnames';
import { MobileAnalysisGlobalSettings } from './mobileAnalysisGlobalSettings';

const { Modal } = overlays;

function ContextDisplay() {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [context, setContext] = useState<Context>();
  const [requiresEdit, setRequiresEdit] = useState<SettingDefinition | undefined>();
  const { isMobile } = useMobile();

  useEffect(() => {
    const loadingSubscriber = isLoading => {
      setIsLoading(isLoading);
    };
    const changeSubscriber = (context: Context) => {
      if (context) {
        setContext(context);
      }
    };
    const requiresEditSubscriber = (definition?: SettingDefinition) => {
      setRequiresEdit(definition);
    };
    ContextManager.getInstance().subscribeToLoading(loadingSubscriber);
    ContextManager.getInstance().subscribeToSettingsChanges(changeSubscriber);
    ContextManager.getInstance().subscribeToEditRequired(requiresEditSubscriber);

    return () => {
      ContextManager.getInstance().unsubscribeFromLoading(loadingSubscriber);
      ContextManager.getInstance().unsubscribeFromSettingsChanges(changeSubscriber);
      ContextManager.getInstance().unsubscribeFromEditRequired(requiresEditSubscriber);
    };
  }, []);

  if (!context) {
    return null;
  }

  const {
    settings: { definitions, values },
  } = context;
  const selectable = definitions.filter(def => def.selection.isSelectable);
  const primary = selectable.filter(def => def.selection.isSecondary !== true);
  const secondary = selectable.filter(def => def.selection.isSecondary === true);

  return (
    <EditProvider>
      <div className={classNames('flex items-center', { 'flex-wrap gap-x-2': !isMobile, 'pl-2 gap-x-1': isMobile })}>
        {primary.map((def, idx) => (
          <span
            key={def.name}
            className={classNames({
              'mobile-markets': idx === 0 && isMobile,
              'mobile-period': idx === 1 && isMobile,
            })}
          >
            <Entry key={def.name} definition={def} values={values} isLoading={isLoading} />
          </span>
        ))}
        {!isMobile ? (
          <div className="flex gap-x-2">
            {secondary.map(def => (
              <Entry key={def.name} definition={def} values={values} isLoading={isLoading} />
            ))}
          </div>
        ) : (
          <MobileAnalysisGlobalSettings settings={secondary} context={context} />
        )}
        {requiresEdit && (
          <EditContextModal
            definition={requiresEdit}
            values={values}
            isLoading={isLoading}
            onClose={() => {
              setRequiresEdit(undefined);
              document.body.style.overflow = 'unset';
            }}
          />
        )}
      </div>
    </EditProvider>
  );
}

type EntryProps = {
  isLoading: boolean;
  definition: SettingDefinition;
  values: SettingsValues;
};
function Entry({ isLoading, definition, values }: EntryProps) {
  const [expanded, setExpanded] = useState<boolean>(false);
  const el = useRef<HTMLDivElement>(null);
  const { isMobile } = useMobile();
  const {
    name,
    labels,
    selection: { selector, isSecondary, metadata },
  } = definition;
  const { cell } = values;
  useOnOutsideClick(el, () => setExpanded(false));
  const { trackUpdates } = useContextTracking();
  const handleChange = async (updates: Record<string, Entity[]>) => {
    ContextManager.getInstance().updateSettingsValues(updates);
    trackUpdates({ tracking: metadata?.tracking }, updates);
  };

  const Selector = ContentProviderRegister.getInstance().getContextSettingsSelector(selector, name);
  const { Icon } = elements;
  const [hovered, setHovered] = useState(false);

  // Returns the display label for the entry, handling mobile and special cases
  const getLabel = () => {
    if (isMobile && name === 'openMarkets' && Array.isArray(cell) && cell.length > 0) {
      return `${cell.length} market${cell.length > 1 ? 's' : ''}`;
    }
    if (isMobile && name === 'focusPeriod' && labels?.label) {
      return labels.label.replace(/\b(\d{4})\b/g, match => match.slice(-2));
    }
    return labels?.label || '';
  };

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} className={`relative`} ref={el}>
      <button
        disabled={isLoading}
        onClick={() => setExpanded(!expanded)}
        className={classNames('flex rounded pt-1 pb-1 text-left', {
          'bg-brand-light': hovered && !expanded,
          'bg-medium text-brand': expanded,
          'text-brand': hovered,
          'text-default': !hovered,
          'pl-2': !isMobile,
          'pl-1': isMobile,
        })}
      >
        <div className={` ${isSecondary ? 'font-normal' : 'font-bold'}`}>
          <div>
            <div className="flex justify-between items-center">
              <span className="context-label">{getLabel()}</span>
              <Icon hoverColor="brand" color={expanded || hovered ? 'brand' : 'secondaryInteractive'}>
                <Icon.icons.ChevronDown />
              </Icon>
            </div>
          </div>
        </div>
      </button>
      {expanded && (
        <Selector
          isLoading={isLoading}
          onChange={handleChange}
          definition={definition}
          values={values[name] || []}
          onClose={() => {
            setHovered(false);
            setExpanded(false);
          }}
        />
      )}
    </div>
  );
}

type EditModalProps = {
  isLoading: boolean;
  definition: SettingDefinition;
  values: SettingsValues;
  onClose: () => void;
};
function EditContextModal({ isLoading, definition, values, onClose }: EditModalProps) {
  const { trackUpdates } = useContextTracking();

  const {
    name,
    labels,
    selection: { selector, metadata },
  } = definition;
  const Selector = ContentProviderRegister.getInstance().getContextSettingsSelector(selector, name);

  const handleChange = (updates: Record<string, Entity[]>) => {
    ContextManager.getInstance().updateSettingsValues(updates);
    trackUpdates({ tracking: metadata?.tracking }, updates);
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose}>
      <Modal.Header>{labels?.identifier || '??NO LABEL??'}</Modal.Header>
      <Selector
        isLoading={isLoading}
        definition={definition}
        values={values[name] || []}
        onChange={handleChange}
        onClose={onClose}
      />
    </Modal>
  );
}

export { ContextDisplay, Entry };
